import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useSearch } from '@tanstack/react-router'
import { ChevronLeft, Loader2, Mail } from 'lucide-react'
import { useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { PasswordInput } from '@/_components/inputs/password-input'
import { SignUpInputDTOSchema } from '@doutorsim/shared/auth'
import { Button } from '../../../_components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../_components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../_components/ui/form'
import { Input } from '../../../_components/ui/input'
import { api } from '../../_lib/api'
import { AuthLayout } from '../_components/auth-layout'
import { AuthLegalLinks } from '../_components/auth-legal-links'

const signupFormSchema = z
  .object({
    name: SignUpInputDTOSchema.shape.name,
    email: z
      .string({
        error: 'O e-mail é obrigatório.',
      })
      .trim()
      .email({
        message: 'Insira um e-mail válido.',
      }),
    password: z
      .string({
        error: 'A senha é obrigatória.',
      })
      .trim()
      .min(8, {
        message: 'A senha deve ter no mínimo 8 caracteres.',
      }),
    passwordConfirmation: z
      .string({
        error: 'A confirmação da senha é obrigatória.',
      })
      .trim()
      .min(8, {
        message: 'A confirmação da senha deve ter no mínimo 8 caracteres.',
      }),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: 'As senhas não coincidem.',
    path: ['passwordConfirmation'],
  })

type SignUpForm = z.infer<typeof signupFormSchema>

const SignUpPage = () => {
  const [isPending, startTransition] = useTransition()
  const [registeredEmail, setRegisteredEmail] = useState<string>()
  const searchParams = useSearch({ strict: false }) as { invitationId?: string }

  const loginSearch = searchParams.invitationId ? { invitationId: searchParams.invitationId } : {}

  const methods = useForm<SignUpForm>({
    resolver: zodResolver(signupFormSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      passwordConfirmation: '',
    },
  })

  const onSubmit = async (data: SignUpForm) => {
    startTransition(async () => {
      const response = await api.auth.signup({
        name: data.name,
        email: data.email,
        password: data.password,
      })
      if (!response?.data) {
        console.error(response?.error)
        toast.error('Erro ao criar conta', {
          description: 'Por favor, tente novamente.',
        })
        return
      }

      setRegisteredEmail(data.email)
    })
  }

  if (registeredEmail) {
    return (
      <AuthLayout>
        <Card className="w-full max-w-[420px] border-0 p-8 shadow-[0_12px_48px_rgba(26,33,52,0.11)]">
          <CardHeader className="p-0 pb-6 text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-brand-green-primary-1">
              <Mail className="h-6 w-6 text-brand-green-primary-5" />
            </div>
            <CardTitle>Conta criada com sucesso!</CardTitle>
            <CardDescription>
              Enviamos um link de verificação para{' '}
              <span className="font-medium text-foreground">{registeredEmail}</span>. Verifique sua
              caixa de entrada para ativar sua conta.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Button asChild className="w-full">
              <Link to="/login" search={loginSearch}>
                Ir para Login
              </Link>
            </Button>
          </CardContent>

          <AuthLegalLinks />
        </Card>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-[420px] border-0 p-8 shadow-[0_12px_48px_rgba(26,33,52,0.11)]">
        {searchParams.invitationId ? (
          <Link
            to="/login/accept-invitation/$invitationId"
            params={{ invitationId: searchParams.invitationId }}
            className="mb-2 flex w-fit items-center gap-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronLeft size={14} />
            Voltar
          </Link>
        ) : (
          <Link
            to="/login"
            className="mb-2 flex w-fit items-center gap-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronLeft size={14} />
            Voltar
          </Link>
        )}

        <CardHeader className="p-0 pb-6">
          <CardTitle>Criar conta</CardTitle>
          <CardDescription>Crie sua conta para acessar nosso sistema.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Form {...methods}>
            <form
              onSubmit={methods.handleSubmit((data) => onSubmit(data))}
              className="flex flex-col gap-5"
            >
              <FormField
                control={methods.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input placeholder="Insira seu nome completo" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={methods.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Insira seu email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={methods.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Senha</FormLabel>
                    <FormControl>
                      <PasswordInput placeholder="Mínimo 8 caracteres" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={methods.control}
                name="passwordConfirmation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmar senha</FormLabel>
                    <FormControl>
                      <PasswordInput placeholder="Repita a senha" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Criar conta
              </Button>
            </form>
          </Form>

          <AuthLegalLinks />
        </CardContent>
      </Card>
    </AuthLayout>
  )
}

export default SignUpPage
